import React from 'react';

// --- 1. CONFIGURATION INTERFACE ---

/**
 * Defines the structure for a single metric card's data.
 */
export interface LeadMetric {
  id: string;
  metric: string; // The main number/percentage (e.g., '223', '95%')
  label: string; // The text description (e.g., 'Leads Generated')
  change: string; // The percentage change value (e.g., '+5.6%', '-1.6%')
  isPositive: boolean; // True for green/up, False for red/down
  iconBgClass: string; // Tailwind class for the icon background (e.g., 'bg-teal-100')
  iconTextClass: string; // Tailwind class for the icon text (e.g., 'text-teal-600')
  iconSymbol: string; // A placeholder symbol or the actual icon component/path
}

// --- 2. CONFIGURATION DATA ---

/**
 * Default data set for the Leads Performance dashboard.
 */
export const leadsData: LeadMetric[] = [
  {
    id: 'generated',
    metric: '223',
    label: 'Leads Generated',
    change: '+5.6%',
    isPositive: true,
    iconBgClass: 'bg-teal-100',
    iconTextClass: 'text-teal-600',
    iconSymbol: 'A',
  },
  {
    id: 'converted',
    metric: '223',
    label: 'Leads Converted',
    change: '+5.6%',
    isPositive: true,
    iconBgClass: 'bg-purple-100',
    iconTextClass: 'text-purple-600',
    iconSymbol: 'P',
  },
  {
    id: 'closed',
    metric: '223',
    label: 'Leads Closed',
    change: '+5.6%',
    isPositive: true,
    iconBgClass: 'bg-blue-100',
    iconTextClass: 'text-blue-600',
    iconSymbol: 'D',
  },
  {
    id: 'lost',
    metric: '223',
    label: 'Leads Lost',
    change: '+5.6%',
    isPositive: true,
    iconBgClass: 'bg-red-100',
    iconTextClass: 'text-red-600',
    iconSymbol: 'R',
  },
  {
    id: 'conversionRate',
    metric: '95%',
    label: 'Conversion Rate',
    change: '+5.6%',
    isPositive: true,
    iconBgClass: 'bg-green-100',
    iconTextClass: 'text-green-600',
    iconSymbol: '✓',
  },
  {
    id: 'lossRate',
    metric: '5%',
    label: 'Loss Rate',
    change: '-1.6%',
    isPositive: false,
    iconBgClass: 'bg-red-100',
    iconTextClass: 'text-red-600',
    iconSymbol: '⇃',
  },
];

// --- 3. REUSABLE CARD COMPONENT ---

interface LeadMetricCardProps {
  data: LeadMetric;
}

const LeadMetricCard: React.FC<LeadMetricCardProps> = ({ data }) => {
  // Determine color classes and arrow icon for the change indicator
  const changeColor = data.isPositive ? 'text-green-600' : 'text-red-600';
  const arrowIcon = data.isPositive ? '▲' : '▼'; // Unicode arrow up/down

  return (
    <div className="flex flex-col bg-white rounded-xl shadow-sm p-4 w-full h-full border border-gray-100 hover:shadow-md transition duration-300">
      
      {/* Icon and Metric */}
      <div className="flex items-center space-x-3 mb-2">
        {/* Icon Circle (Matches the visual style of the image) */}
        <div 
          className={`flex items-center justify-center h-10 w-10 rounded-lg text-xl font-bold ${data.iconBgClass} ${data.iconTextClass}`}
        >
          <span className="text-lg">{data.iconSymbol}</span>
        </div>
        
        {/* Metric Value */}
        <div className="text-3xl font-semibold text-gray-800">
          {data.metric}
        </div>
      </div>

      {/* Label and Change Indicator */}
      <div className="flex flex-col mt-auto ">
        {/* Label */}
        <p className="text-sm text-gray-500 mb-1">
          {data.label}
        </p>

        {/* Change Indicator */}
        <div className={`flex items-center text-xs justify-end font-medium ${changeColor}`}>
          <span className="mr-1">{data.change}</span>
          {/* Using a smaller text size class for the arrow to look more proportionate */}
          <span className="text-[10px]">{arrowIcon}</span> 
        </div>
      </div>
    </div>
  );
};

// --- 4. MAIN CONTAINER COMPONENT ---

interface LeadsPerformanceProps {
  /**
   * The array of metric data to display. Defaults to leadsData if not provided.
   */
  metrics?: LeadMetric[];
}


const LeadsPerformance: React.FC<LeadsPerformanceProps> = ({ 
  metrics = leadsData 
}) => {
  return (
    <div className="">
      
      {/* Title */}
      <h2 className="text-xl font-semibold text-gray-800 ">
        Leads Performance
      </h2>

     
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 mb-6">
        {metrics.map((metric) => (
          <LeadMetricCard key={metric.id} data={metric} />
        ))}
      </div>
      
  
    
    </div>
  );
};

export default LeadsPerformance;